/* ==================== Addstudent.jsx ==================== */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faCamera,
  faUndo,
  faRedo,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import "./Addstudent.css";

const API_URL = "https://aadyaacademy.org/api";

function Addstudent({ handleCancel, fetchData, editingStudent }) {
  const [newStudent, setNewStudent] = useState({
    student_id: "",
    student_name: "",
    student_aadhar: "",
    class: "",
    section: "",
    gender: "",
    dob: "",
    student_blood_group: "",
    academic_year_joined: "",
    caste: "",
    nationality: "",
    mother_tongue: "",
    address: "",
    home_town: "",
    father_name: "",
    father_phone: "",
    father_email: "",
    father_occupation: "",
    father_aadhar: "",
    mother_name: "",
    mother_email: "",
    mother_phone: "",
    mother_occupation: "",
    mother_aadhar: "",
    guardian_name: "",
    guardian_phone: "",
    guardian_email: "",
    guardian_aadhar: "",
    student_image: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  // ---- image editing state ----
  const [showAdjust, setShowAdjust] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const originalImageRef = useRef(null); // **resized** base64 (no adjustments)
  const canvasRef = useRef(null); // hidden canvas for final output

  /* ------------------------------------------------- */
  /* Populate form when editing */
  /* ------------------------------------------------- */
  useEffect(() => {
    if (editingStudent) {
      const normalizedGender =
        editingStudent.gender === "M"
          ? "male"
          : editingStudent.gender === "F"
          ? "female"
          : "";
      const imageUrl = editingStudent.image
        ? `${API_URL}${editingStudent.image.startsWith("/") ? "" : "/"}${editingStudent.image}`
        : "";
      setNewStudent({
        student_id: editingStudent.student_id || "",
        student_name: editingStudent.student_name || "",
        student_aadhar: editingStudent.student_aadhar || "",
        class: editingStudent.class || "",
        section: editingStudent.section || "",
        gender: normalizedGender,
        dob: editingStudent.dob || "",
        student_blood_group: editingStudent.student_blood_group || "",
        academic_year_joined: editingStudent.academic_year_joined || "",
        caste: editingStudent.caste || "",
        nationality: editingStudent.nationality || "",
        mother_tongue: editingStudent.mother_tongue || "",
        address: editingStudent.address || "",
        home_town: editingStudent.home_town || "",
        father_name: editingStudent.father_name || "",
        father_phone: editingStudent.father_phone || "",
        father_email: editingStudent.father_email || "",
        father_occupation: editingStudent.father_occupation || "",
        father_aadhar: editingStudent.father_aadhar || "",
        mother_name: editingStudent.mother_name || "",
        mother_email: editingStudent.mother_email || "",
        mother_phone: editingStudent.mother_phone || "",
        mother_occupation: editingStudent.mother_occupation || "",
        mother_aadhar: editingStudent.mother_aadhar || "",
        guardian_name: editingStudent.guardian_name || "",
        guardian_phone: editingStudent.guardian_phone || "",
        guardian_email: editingStudent.guardian_email || "",
        guardian_aadhar: editingStudent.guardian_aadhar || "",
        student_image: imageUrl,
      });
      if (imageUrl) {
        setImagePreview(imageUrl);
        originalImageRef.current = imageUrl;
      }
    }
  }, [editingStudent]);

  /* ------------------------------------------------- */
  /* Fetch classes & sections */
  /* ------------------------------------------------- */
  useEffect(() => {
    const fetchClassesAndSections = async () => {
      try {
        const [classResponse, sectionResponse] = await Promise.all([
          axios.get(`${API_URL}/classes`),
          axios.get(`${API_URL}/sections`),
        ]);
        setClasses(classResponse.data?.classes || []);
        setSections(sectionResponse.data?.sections || []);
      } catch (error) {
        console.error("Error fetching classes and sections:", error);
        toast.error("Failed to load classes and sections", { autoClose: 2000 });
      }
    };
    fetchClassesAndSections();
  }, []);

  /* ------------------------------------------------- */
  /* Image Resize (max 800×800) & Validation */
  /* ------------------------------------------------- */
  const resizeImage = (file, maxWidth = 800, maxHeight = 800) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxWidth) {
              height = (maxWidth * height) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (maxHeight * width) / height;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.onerror = () => reject(new Error("Image load error"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("FileReader error"));
      reader.readAsDataURL(file);
    });
  };

  const processImageFile = async (file) => {
    if (!file) return;
    const fileSize = file.size / 1024 / 1024; // MB
    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPEG and PNG files are allowed", { autoClose: 2000 });
      return;
    }
    if (fileSize > 2) {
      toast.error("File size should not exceed 2 MB", { autoClose: 2000 });
      return;
    }
    try {
      const resizedBase64 = await resizeImage(file);
      originalImageRef.current = resizedBase64;
      setImagePreview(resizedBase64);
      setNewStudent((prev) => ({ ...prev, student_image: resizedBase64 }));
    } catch (err) {
      console.error(err);
      toast.error("Error processing image", { autoClose: 2000 });
    }
  };

  const handleGalleryChange = (e) => {
    const file = e.target.files[0];
    if (file) processImageFile(file);
  };
  const handleCameraChange = (e) => {
    const file = e.target.files[0];
    if (file) processImageFile(file);
  };

  /* ------------------------------------------------- */
  /* Open adjustment panel */
  /* ------------------------------------------------- */
  const openAdjustPanel = () => {
    if (!originalImageRef.current) return;
    setZoom(1);
    setRotation(0);
    setPanX(0);
    setPanY(0);
    setShowAdjust(true);
  };

  /* ------------------------------------------------- */
  /* Apply adjustments & generate final base64 */
  /* ------------------------------------------------- */
  const applyAdjustments = () => {
    if (!originalImageRef.current) return;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current || document.createElement("canvas");
      if (!canvasRef.current) canvasRef.current = canvas;
      const size = 150;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.translate(size / 2, size / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);
      ctx.translate(-size / 2 + panX, -size / 2 + panY);
      ctx.drawImage(img, 0, 0, size, size);
      ctx.restore();
      const finalBase64 = canvas.toDataURL("image/jpeg", 0.8);
      setImagePreview(finalBase64);
      setNewStudent((prev) => ({ ...prev, student_image: finalBase64 }));
      setShowAdjust(false);
    };
    img.src = originalImageRef.current;
  };

  const resetAdjustments = () => {
    setZoom(1);
    setRotation(0);
    setPanX(0);
    setPanY(0);
    setImagePreview(originalImageRef.current);
    setNewStudent((prev) => ({ ...prev, student_image: originalImageRef.current }));
  };

  /* ------------------------------------------------- */
  /* Input handlers */
  /* ------------------------------------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStudent((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = () => {
    setNewStudent({
      student_id: "",
      student_name: "",
      student_aadhar: "",
      class: "",
      section: "",
      gender: "",
      dob: "",
      student_blood_group: "",
      academic_year_joined: "",
      caste: "",
      nationality: "",
      mother_tongue: "",
      address: "",
      home_town: "",
      father_name: "",
      father_phone: "",
      father_email: "",
      father_occupation: "",
      father_aadhar: "",
      mother_name: "",
      mother_email: "",
      mother_phone: "",
      mother_occupation: "",
      mother_aadhar: "",
      guardian_name: "",
      guardian_phone: "",
      guardian_email: "",
      guardian_aadhar: "",
      student_image: "",
    });
    setImagePreview(null);
    setShowAdjust(false);
    originalImageRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (showAdjust) applyAdjustments();
    try {
      const payload = {
        ...newStudent,
        gender:
          newStudent.gender === "male"
            ? "M"
            : newStudent.gender === "female"
            ? "F"
            : "",
      };
      if (payload.student_image && payload.student_image.startsWith("http")) {
        delete payload.student_image;
      }
      if (editingStudent) {
        await axios.put(`${API_URL}/students/${editingStudent.student_id}`, payload);
        toast.success("Student updated successfully!", { autoClose: 2000 });
      } else {
        await axios.post(`${API_URL}/students`, payload);
        toast.success("Student added successfully!", { autoClose: 2000 });
      }
      fetchData();
      handleCancel();
    } catch (error) {
      console.error("Error saving student:", error);
      if (error.response?.status === 409) {
        toast.error(error.response.data.message || "Student ID already exists!", {
          autoClose: 2000,
        });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message || "Invalid data provided!", {
          autoClose: 2000,
        });
      } else {
        toast.error("Error saving student! Please try again.", { autoClose: 2000 });
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const academicYearJoined = Array.from(
    { length: 7 },
    (_, i) => `${currentYear - 5 + i}-${currentYear - 4 + i}`
  );

  /* ------------------------------------------------- */
  /* JSX */
  /* ------------------------------------------------- */
  return (
    <div className="add-student-container">
      <div className="add-stu-c">
        <h2>{editingStudent ? "Edit Student" : "Add New Student"}</h2>
        <form onSubmit={handleSubmit}>
          {/* ---------- IMAGE ---------- */}
          <div className="add-img">
            {/* Preview / Placeholder */}
            <div className="img-preview-wrapper">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Student Preview"
                  className="student-preview-img"
                  style={{
                    transform: showAdjust
                      ? `scale(${zoom}) rotate(${rotation}deg) translate(${panX}px, ${panY}px)`
                      : "none",
                    transition: showAdjust ? "none" : "transform 0.2s",
                  }}
                />
              ) : (
                <div className="no-img-placeholder">No image selected</div>
              )}
            </div>

            {/* Upload controls + Adjust button */}
            <div className="upload-controls">
              <button
                type="button"
                className="gallery-btn"
                onClick={() => document.getElementById("galleryInput").click()}
              >
                Choose from Gallery
              </button>
              <button
                type="button"
                className="camera-btn"
                onClick={() => document.getElementById("cameraInput").click()}
              >
                <FontAwesomeIcon icon={faCamera} /> Take Photo
              </button>

              {imagePreview && !showAdjust && (
                <button
                  type="button"
                  className="adjust-btn"
                  onClick={openAdjustPanel}
                >
                  <FontAwesomeIcon icon={faEdit} /> Adjust Image
                </button>
              )}
            </div>

            {/* Hidden file inputs */}
            <input
              id="galleryInput"
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleGalleryChange}
              className="hidden-file-input"
            />
            <input
              id="cameraInput"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraChange}
              className="hidden-file-input"
            />
          </div>

          {/* ---------- IMAGE ADJUSTMENT PANEL (REDESIGNED) ---------- */}
          {showAdjust && (
            <div className="image-adjust-panel">
              <div className="adjust-header">
                <h4>Adjust Image</h4>
                <button
                  type="button"
                  className="close-adjust"
                  onClick={() => setShowAdjust(false)}
                >
                  ×
                </button>
              </div>

              <div className="adjust-row">
                <label>Zoom</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                />
                <span>{zoom.toFixed(1)}x</span>
              </div>

              <div className="adjust-row">
                <label>Rotate</label>
                <button
                  type="button"
                  className="rotate-btn"
                  onClick={() => setRotation((r) => r - 90)}
                >
                  <FontAwesomeIcon icon={faUndo} />
                </button>
                <span>{rotation}°</span>
                <button
                  type="button"
                  className="rotate-btn"
                  onClick={() => setRotation((r) => r + 90)}
                >
                  <FontAwesomeIcon icon={faRedo} />
                </button>
              </div>

              <div className="adjust-row">
                <label>Pan X</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={panX}
                  onChange={(e) => setPanX(parseInt(e.target.value))}
                />
                <span>{panX}px</span>
              </div>

              <div className="adjust-row">
                <label>Pan Y</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={panY}
                  onChange={(e) => setPanY(parseInt(e.target.value))}
                />
                <span>{panY}px</span>
              </div>

              <div className="adjust-actions">
                <button type="button" className="apply-btn" onClick={applyAdjustments}>
                  Apply
                </button>
                <button type="button" className="reset-btn" onClick={resetAdjustments}>
                  Reset
                </button>
                <button type="button" className="cancel-btn" onClick={() => setShowAdjust(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ---------- ROW 1 ---------- */}
          <div className="ad-f-n">
            <div className="field-wrapper">
              <h4>
                Student ID <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                type="text"
                name="student_id"
                value={newStudent.student_id}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^0-9/-]/g, "");
                  if (v.length > 3 && v.charAt(3) !== "/")
                    v = v.slice(0, 3) + "/" + v.slice(3);
                  if (v.length > 8 && v.charAt(8) !== "-")
                    v = v.slice(0, 8) + "-" + v.slice(8);
                  if (v.length > 11) v = v.slice(0, 11);
                  handleInputChange({ target: { name: "student_id", value: v } });
                }}
                placeholder="200/2024-25"
                required
              />
            </div>
            <div className="field-wrapper">
              <h4>
                Student Name <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                type="text"
                name="student_name"
                value={newStudent.student_name}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: "student_name",
                      value: e.target.value.replace(/\d/g, ""),
                    },
                  })
                }
                placeholder="Enter"
                required
              />
            </div>
            <div className="field-wrapper">
              <h4>Student Aadhar Number</h4>
              <input
                type="text"
                name="student_aadhar"
                value={newStudent.student_aadhar}
                onChange={(e) => {
                  if (/^\d{0,12}$/.test(e.target.value)) handleInputChange(e);
                }}
                placeholder="Enter"
                maxLength={12}
                inputMode="numeric"
              />
            </div>
            <div className="field-wrapper">
              <h4>
                Class <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select name="class" value={newStudent.class} onChange={handleInputChange} required>
                <option value="">Select Class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-wrapper">
              <h4>Section</h4>
              <select name="section" value={newStudent.section} onChange={handleInputChange}>
                <option value="">Select Section</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ---------- ROW 2 ---------- */}
          <div className="c-g-d">
            <div className="field-wrapper">
              <h4>
                Gender <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select name="gender" value={newStudent.gender} onChange={handleInputChange} required>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="field-wrapper">
              <h4>
                Date of Birth <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                type="date"
                name="dob"
                value={newStudent.dob}
                onChange={handleInputChange}
                max={new Date().toISOString().split("T")[0]}
                required
              />
            </div>
            <div className="field-wrapper">
              <h4>Blood Group</h4>
              <input
                type="text"
                name="student_blood_group"
                value={newStudent.student_blood_group}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  if (/^(A|B|AB|O)?[+-]?$/.test(v)) {
                    handleInputChange({ target: { name: "student_blood_group", value: v } });
                  }
                }}
                placeholder="Enter"
              />
            </div>
          </div>

          {/* ---------- ROW 3 ---------- */}
          <div className="c-n-m">
            <div className="field-wrapper">
              <h4>
                Joined Academic Year <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select
                name="academic_year_joined"
                value={newStudent.academic_year_joined}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Joined Academic Year</option>
                {academicYearJoined.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-wrapper">
              <h4>Caste</h4>
              <select name="caste" value={newStudent.caste} onChange={handleInputChange}>
                <option value="">Select</option>
                <option value="General">General</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="OBC">OBC</option>
              </select>
            </div>
            <div className="field-wrapper">
              <h4>Nationality</h4>
              <input
                type="text"
                name="nationality"
                value={newStudent.nationality}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: "nationality",
                      value: e.target.value.replace(/\d/g, ""),
                    },
                  })
                }
                placeholder="Enter"
              />
            </div>
            <div className="field-wrapper">
              <h4>Mother Tongue</h4>
              <input
                type="text"
                name="mother_tongue"
                value={newStudent.mother_tongue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: "mother_tongue",
                      value: e.target.value.replace(/\d/g, ""),
                    },
                  })
                }
                placeholder="Enter"
              />
            </div>
          </div>

          {/* ---------- ADDRESS & HOMETOWN ---------- */}
          <div className="h-a">
            <div className="field-wrapper full">
              <h4>Address</h4>
              <textarea
                name="address"
                value={newStudent.address}
                onChange={handleInputChange}
                placeholder="Enter"
              />
            </div>
          </div>
          <div className="h-a">
            <div className="field-wrapper full">
              <h4>Home Town</h4>
              <input
                type="text"
                name="home_town"
                value={newStudent.home_town}
                onChange={handleInputChange}
                placeholder="Enter"
              />
            </div>
          </div>

          {/* ---------- PARENTS / GUARDIAN ---------- */}
          <div className="parent">
            {/* FATHER */}
            <div className="parent-col">
              <div className="field-wrapper">
                <h4>
                  Father's Name <FontAwesomeIcon icon={faStar} className="stu-icon" />
                </h4>
                <input
                  type="text"
                  name="father_name"
                  value={newStudent.father_name}
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: "father_name",
                        value: e.target.value.replace(/\d/g, ""),
                      },
                    })
                  }
                  placeholder="Enter"
                  required
                />
              </div>
              <div className="field-wrapper">
                <h4>
                  Father's Phone Number <FontAwesomeIcon icon={faStar} className="stu-icon" />
                </h4>
                <input
                  type="text"
                  name="father_phone"
                  value={newStudent.father_phone}
                  onChange={(e) => {
                    if (/^\d{0,10}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={10}
                  required
                />
              </div>
              <div className="field-wrapper">
                <h4>Father's Email</h4>
                <input
                  type="email"
                  name="father_email"
                  value={newStudent.father_email}
                  onChange={handleInputChange}
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Father's Occupation</h4>
                <input
                  type="text"
                  name="father_occupation"
                  value={newStudent.father_occupation}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Father's Aadhar Number</h4>
                <input
                  type="text"
                  name="father_aadhar"
                  value={newStudent.father_aadhar}
                  onChange={(e) => {
                    if (/^\d{0,12}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={12}
                />
              </div>
            </div>

            {/* MOTHER */}
            <div className="parent-col">
              <div className="field-wrapper">
                <h4>Mother's Name</h4>
                <input
                  type="text"
                  name="mother_name"
                  value={newStudent.mother_name}
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: "mother_name",
                        value: e.target.value.replace(/\d/g, ""),
                      },
                    })
                  }
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Mother's Phone Number</h4>
                <input
                  type="text"
                  name="mother_phone"
                  value={newStudent.mother_phone}
                  onChange={(e) => {
                    if (/^\d{0,10}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={10}
                />
              </div>
              <div className="field-wrapper">
                <h4>Mother's Email</h4>
                <input
                  type="email"
                  name="mother_email"
                  value={newStudent.mother_email}
                  onChange={handleInputChange}
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Mother's Occupation</h4>
                <input
                  type="text"
                  name="mother_occupation"
                  value={newStudent.mother_occupation}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Mother's Aadhar Number</h4>
                <input
                  type="text"
                  name="mother_aadhar"
                  value={newStudent.mother_aadhar}
                  onChange={(e) => {
                    if (/^\d{0,12}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={12}
                />
              </div>
            </div>

            {/* GUARDIAN */}
            <div className="parent-col">
              <div className="field-wrapper">
                <h4>Guardian's Name</h4>
                <input
                  type="text"
                  name="guardian_name"
                  value={newStudent.guardian_name}
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: "guardian_name",
                        value: e.target.value.replace(/\d/g, ""),
                      },
                    })
                  }
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Guardian's Phone Number</h4>
                <input
                  type="text"
                  name="guardian_phone"
                  value={newStudent.guardian_phone}
                  onChange={(e) => {
                    if (/^\d{0,10}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={10}
                />
              </div>
              <div className="field-wrapper">
                <h4>Guardian's Email</h4>
                <input
                  type="email"
                  name="guardian_email"
                  value={newStudent.guardian_email}
                  onChange={handleInputChange}
                  placeholder="Enter"
                />
              </div>
              <div className="field-wrapper">
                <h4>Guardian's Aadhar Number</h4>
                <input
                  type="text"
                  name="guardian_aadhar"
                  value={newStudent.guardian_aadhar}
                  onChange={(e) => {
                    if (/^\d{0,12}$/.test(e.target.value)) handleInputChange(e);
                  }}
                  placeholder="Enter"
                  maxLength={12}
                />
              </div>
            </div>
          </div>

          {/* ---------- BUTTONS ---------- */}
          <div className="add-stu-btn">
            <button className="add-stu-btn-submit" type="submit">
              {editingStudent ? "Update" : "Add"}
            </button>
            <button className="add-stu-btn-clear" type="button" onClick={handleClear}>
              Clear
            </button>
            <button className="add-stu-btn-cancel" type="button" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
      {/* hidden canvas for final image */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

export default Addstudent;